import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";

export type LinkReadState<R> =
  R | { kind: "loading" } | { kind: "error"; message: string };

/** One token read and its reload: loading, what the read answered (the page, gone (410), not found (404), or for
 *  a proof link a replaced proof), or a failure to show. `key` names what is read (the token and any quote
 *  number); a new key reads again. */
export function useLinkRead<R extends { kind: string }>(
  key: string,
  read: () => Promise<R>,
  failureSentence: string,
): { state: LinkReadState<R>; reload: () => Promise<void> } {
  const [state, setState] = useState<LinkReadState<R>>({ kind: "loading" });
  const readRef = useRef(read);
  readRef.current = read;
  const reload = useCallback(async () => {
    try {
      setState(await readRef.current());
    } catch (err) {
      setState({
        kind: "error",
        message: mutationErrorMessage(err, failureSentence),
      });
    }
  }, [failureSentence]);
  useEffect(() => {
    setState({ kind: "loading" });
    void reload();
  }, [key, reload]);
  return { state, reload };
}

/** Go to a URL the backend handed out: one of this app's pages in place, anything else (Stripe's checkout, an
 *  absolute link to the order page) as a full navigation. */
export function useGoTo(): (url: string) => void {
  const navigate = useNavigate();
  const capabilities = usePlatformCapabilities();
  return (url: string) => {
    if (url.startsWith("/")) navigate(url);
    else capabilities.redirectTo(url);
  };
}
